/**
 * @render react
 * @name SideContent
 * @example
 * <SideContent title="Title of Side Content"><Heading size={2}>Hello World</Heading></SideContent>
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Heading } from 'Components/molecules/Heading';

import './_style.scss';

/**
 * SideContent component
 * @param {string} title - Title of the component
 * @param {number} titleSize - Size of the Heading (default h4)
 * @param {string} subtitle - Sub title of the component
 * @param {node} children - Any children to show within the component
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} context - Extra information coming from Polaris provider.
 */

const SideContent = (
  {
    title,
    titleSize,
    subtitle,
    children,
    loadStyles,
    extraClassNames,
    context: { cssPrefix },
  },
) => (
  <div
    className={classNames({
      [`${cssPrefix}__side-content`]: loadStyles,
      ...extraClassNames,
    })}
  >
    {title && (
      <div className={`${cssPrefix}__side-content--title`}>
        <Heading size={titleSize}>
          {title}
          {' '}
          {subtitle && (
            <span className={`${cssPrefix}__side-content--subtitle`}>{subtitle}</span>
          )}
        </Heading>
      </div>
    )}
    {children && (
      <div className={`${cssPrefix}__side-content--children`}>
        {children}
      </div>
    )}
  </div>
);

/**
 * Valid props
 */
SideContent.propTypes = {
  title: PropTypes.string,
  titleSize: PropTypes.number,
  subtitle: PropTypes.string,
  children: PropTypes.node,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
};

/**
 * Default props
 */
SideContent.defaultProps = {
  title: '',
  titleSize: 4,
  subtitle: '',
  children: '',
  loadStyles: true,
  extraClassNames: {},
  context: {},
};

export default withPolaris(SideContent);
